import { useEffect, useLayoutEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../utils/constants';
import { DndContext, closestCenter } from '@dnd-kit/core';
import useLauncherDnD from '../../hooks/homeLauncher/useLauncherDnD';
import { useAuth } from '../../contexts/AuthContext';
import useResolvedBackgroundUrl from '../../services/workspaceRuntime/useResolvedBackgroundUrl';
import { useIncident } from '../../contexts/IncidentContext';
import { execute as executeCommand } from '../../services/commandExecutor';
import { toast } from 'react-toastify';
import IconGrid from './components/IconGrid';
import ButtonAdminModal from './components/ButtonAdminModal';
import RtpPagingDialog from './components/RtpPagingDialog';
import RtpPagingAdvancedDialog from './components/RtpPagingAdvancedDialog';
import SipPagingDialog from './components/SipPagingDialog';
import { flowsApi } from '../../services/flowsApiService';
import {
  executeDirectAction,
  indexFlowsById,
  launcherIconVisible,
} from '../../services/homeLauncher/launcherActionService';
import { flowDisabledUserMessage } from '../../utils/flowEnabled';
import {
  waitingDisplayLabel,
  isFailedExecutionResult,
  flowExecutionFailureMessage,
} from '../../utils/executionStatusDisplay';
import { ADD_HOME_ICON_EVENT } from '../../utils/chromeEvents';
import {
  dispatchAction,
  fetchWithAuth,
  getMyImageDisplayActions,
  getTenantDevices,
  createImageDisplayUploadUrl,
  dispatchImageDisplayCommand,
  isOption2ImageDisplayEnabled,
  getTenantSipPagingMyZones,
  getCollaborationWorkspace,
  getContactList,
} from '../../api/backendApi';
import ImageDisplayModal from '../../components/actions/ImageDisplayModal';
import CollaborationWorkspaceModal from '../../components/collaboration/CollaborationWorkspaceModal';
import ContactListViewerDialog from '../../components/contactList/ContactListViewerDialog';
import ContactListCallDialog from '../../components/contactList/ContactListCallDialog';
import PhoneOverlay from '../../components/phone/PhoneOverlay';
import RemindersOverlay from '../../components/reminders/RemindersOverlay';
import EmailRemOverlay from '../../components/emailRem/EmailRemOverlay';
import { isPhoneEnabledForUser } from '../../utils/phoneEntitlement';
import { isRemindersEnabledForUser } from '../../utils/remindersEntitlement';
import { isEmailRemEnabledForUser } from '../../utils/emailRemEntitlement';
import { resolveAndLoad } from '../../services/workspaceRuntime/workspaceRuntimeService';
import { openScreenSaver } from '../../components/ambient/screenSaverNavigation';
import { putImageToSignedUrl } from '../../utils/imageDisplayUpload';
import { LAUNCHER_GRID, clampGridColumns, clampGridRows } from '../../utils/launcherGrid';
import {
  overlayLauncherChrome,
  resolveUserWorkspaceLayout,
  loadLayoutFromApi,
  resolveLauncherBackgroundUrlAsync,
  LAUNCHER_BACKGROUND_CHANGED,
  readLayoutFromStorage,
  readLegacyLayoutFromStorage,
  layoutOwnedByContext,
  writeLayoutToStorage,
} from '../../services/homeLauncher/launcherPersistenceService';
import HomeSetupGuide from './components/HomeSetupGuide';
import {
  canConfigureTenantHome,
  guidanceDismissalKey,
  homeGuidanceFinished,
  homeSetupCardVisible,
  homeGuidanceProgress,
} from './homeSetupGuidance';
import { useDealerAdmin } from '../../contexts/DealerAdminContext';
import SupportContextBanner from '../../components/layout/SupportContextBanner';
import {
  readSupportedTenantSession,
  subscribeSupportedTenantSession,
  writeSupportedTenantSession,
} from '../Admin/supportedTenantsSession';
import { persistHomeLayout } from '../../services/homeLauncher/persistHomeLayout';
import { createNewIcon } from '../../services/homeLauncher/launcherLayoutService';
import {
  interpretLayoutSaveError,
  reconcileHomePreferences,
  remoteHomeOnFocus,
  revisionOf,
  sameHomeRecord,
} from '../../services/homeLauncher/homeLayoutSync';
import { flushPendingLayoutSave } from '../../services/homeLauncher/adapters/legacyLauncherAdapter';
import {
  draftRevisionFor,
  flushDraftSave,
  rememberDraftRevision,
} from '../../services/workspaceRuntime/workspaceDraftService';
import { getDraftVersion } from '../../api/workspaceApi';
import HomePersistenceNotice from './components/HomePersistenceNotice';
import { canEditHomeButtons } from '../../utils/sessionRole';
import {
  RUNTIME_MODE,
  isDraftEditRole,
  legacyRuntimeContext,
} from '../../services/workspaceRuntime/workspaceResolverService';
import { Box, Drawer, IconButton, useMediaQuery, Button } from '@mui/material';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import useUserShortcuts from '../../hooks/shortcuts/useUserShortcuts';
import ShortcutsPanel from './components/ShortcutsPanel';
import {
  loadShortcutsPanelState,
  saveShortcutsPanelState,
} from '../../services/shortcuts/shortcutsPanelLayout';

const EDIT_TIMEOUT_MS = 30000;

export default function HomeLauncherPage() {
  const { currentUser, tenantId: authTenantId, role, userClaims, userData } = useAuth();
  const [supportSession, setSupportSession] = useState(() => readSupportedTenantSession());
  useEffect(() => subscribeSupportedTenantSession(() => {
    setSupportSession(readSupportedTenantSession());
  }), []);
  const tenantId = supportSession?.tenantId || authTenantId;
  const layoutRole = supportSession
    ? (supportSession.accessLevel === 'manage' ? 'tenant_admin' : 'user')
    : role;
  const dealerAdmin = useDealerAdmin() || {};
  const { openIncidentById } = useIncident();
  const navigate = useNavigate();
  const preferredSiteId = userClaims?.siteId || userData?.siteId || null;
  const [icons, setIcons] = useState([]);
  const [flowById, setFlowById] = useState(() => new Map());
  const [editMode, setEditMode] = useState(false);
  const [themePref, setThemePref] = useState('system');
  const [editingIcon, setEditingIcon] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(0);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [layoutStatus, setLayoutStatus] = useState('loading');
  const [layoutRetry, setLayoutRetry] = useState(0);
  const [flowState, setFlowState] = useState('loading');
  const [guidanceDismissed, setGuidanceDismissed] = useState(false);
  const loadedLayoutKeyRef = useRef('');
  const layoutLoadGen = useRef(0);
  const [gridColumns, setGridColumns] = useState(LAUNCHER_GRID.DEFAULT_COLUMNS);
  const [gridRows, setGridRows] = useState(LAUNCHER_GRID.DEFAULT_ROWS);
  const [iconSize, setIconSize] = useState('medium');
  const [gap, setGap] = useState(2);
  const [showGrid, setShowGrid] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const resolvedBackground = useResolvedBackgroundUrl(backgroundImageUrl);
  const [rtpDialogOpen, setRtpDialogOpen] = useState(false);
  const [rtpManualDialogOpen, setRtpManualDialogOpen] = useState(false);
  const [activePagingIcon, setActivePagingIcon] = useState(null);
  const [sipDialogOpen, setSipDialogOpen] = useState(false);
  const [activeSipPagingIcon, setActiveSipPagingIcon] = useState(null);
  const [imageDisplayDialogOpen, setImageDisplayDialogOpen] = useState(false);
  const [activeImageDisplayIcon, setActiveImageDisplayIcon] = useState(null);
  const [imageDisplayLoading, setImageDisplayLoading] = useState(false);
  const [activeSipUri, setActiveSipUri] = useState(null);
  const [collaborationModalOpen, setCollaborationModalOpen] = useState(false);
  const [activeCollaborationData, setActiveCollaborationData] = useState(null);
  const [contactListDialogOpen, setContactListDialogOpen] = useState(false);
  const [activeContactListId, setActiveContactListId] = useState(null);
  const [contactListCallOpen, setContactListCallOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [emailRemOpen, setEmailRemOpen] = useState(false);
  const [activeContactListCall, setActiveContactListCall] = useState(null);
  const [runtimeContext, setRuntimeContext] = useState(null);
  const persistPayloadRef = useRef(null);
  const baseRevisionRef = useRef(0);
  const syncPhaseRef = useRef('idle');
  const [syncPhase, setSyncPhase] = useState('idle');
  const updateSyncPhase = (phase) => {
    syncPhaseRef.current = phase;
    setSyncPhase(phase);
  };
  const [layoutVerified, setLayoutVerified] = useState(false);
  const [syncServer, setSyncServer] = useState(null);
  const editTimeoutRef = useRef(null);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const isDark = themePref === 'dark' || (themePref === 'system' && prefersDark);
  const GRID_COLUMNS = clampGridColumns(gridColumns);
  const GRID_ROWS = clampGridRows(gridRows);
  const GRID_MAX = GRID_COLUMNS * GRID_ROWS;
  const canConfigureHome = supportSession
    ? supportSession.accessLevel === 'manage'
    : canEditHomeButtons({
      role,
      permissions: userData?.permissions,
    });
  const canArrangeLayout = Boolean(currentUser?.uid) && canConfigureHome;
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [shortcutsWidth, setShortcutsWidth] = useState(350);
  const [askSbFocus, setAskSbFocus] = useState(0);
  const dockShortcuts = useMediaQuery('(min-width: 960px)');
  const narrowWorkspace = useMediaQuery('(max-width: 767px)');
  const pageRef = useRef(null);
  const noticeSlotRef = useRef(null);
  const shortcuts = useUserShortcuts({
    uid: currentUser?.uid || null,
    tenantId,
  });

  useEffect(() => {
    const stored = loadShortcutsPanelState(currentUser?.uid || null);
    setShortcutsOpen(stored.open);
    setShortcutsWidth(stored.width);
  }, [currentUser?.uid]);

  const persistShortcutsChrome = useCallback(
    next => {
      const saved = saveShortcutsPanelState(currentUser?.uid || null, next);
      if (next.open != null) setShortcutsOpen(saved.open);
      if (next.width != null) setShortcutsWidth(saved.width);
    },
    [currentUser?.uid]
  );

  const orderedIcons = useMemo(() => {
    return [...icons].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }, [icons]);

  const visibleIcons = useMemo(
    () => icons.filter(icon => launcherIconVisible(icon, {
      editMode,
      flowById,
      keepAssigned: Boolean(runtimeContext?.workspaceId),
    })),
    [icons, editMode, flowById, runtimeContext?.workspaceId]
  );

  const slots = useMemo(() => {
    const arr = new Array(GRID_MAX).fill(null);
    const placeAt = (icon, pos) => {
      const clamped = Math.max(0, Math.min(GRID_MAX - 1, Number.isInteger(pos) ? pos : -1));
      if (clamped >= 0 && !arr[clamped]) {
        arr[clamped] = icon;
        return;
      }
      for (let i = 0; i < GRID_MAX; i++) {
        if (!arr[i]) {
          arr[i] = icon;
          return;
        }
      }
    };
    visibleIcons.forEach(icon => placeAt(icon, icon.position));
    return arr;
  }, [visibleIcons, GRID_MAX]);

  useEffect(() => {
    let mounted = true;
    if (!currentUser?.uid || !tenantId) {
      setFlowById(new Map());
      return () => {
        mounted = false;
      };
    }
    setFlowState('loading');
    flowsApi
      .getAll(tenantId)
      .then(flows => {
        if (!mounted) return;
        setFlowById(indexFlowsById(flows));
        setFlowState('ready');
      })
      .catch(err => {
        console.warn('[HomeLauncher] Could not load Flows for enabled-state visibility', err);
        if (mounted) setFlowState(readSupportedTenantSession()?.tenantId ? 'error' : 'unavailable');
      });
    return () => {
      mounted = false;
    };
  }, [currentUser?.uid, tenantId]);

  const persistSnapshot = useCallback(
    (snapshot, { flush = false, quiet = false } = {}) => {
      persistPayloadRef.current = snapshot;
      const readOnly = readSupportedTenantSession()?.accessLevel === 'view';
      if (readOnly) return Promise.resolve({ readOnly: true });
      if (!quiet) updateSyncPhase('pending');
      const recorded = {
        ...snapshot,
        baseRevision: baseRevisionRef.current,
        revision: baseRevisionRef.current,
      };
      return persistHomeLayout({
        uid: currentUser?.uid,
        role: layoutRole,
        runtimeContext,
        snapshot: recorded,
        flush,
        tenantId,
        readOnly,
      }).then((data) => {
        const nextRevision = revisionOf(data);
        if (nextRevision != null) {
          baseRevisionRef.current = nextRevision;
          if (runtimeContext?.workspaceId) {
            rememberDraftRevision(runtimeContext.workspaceId, nextRevision);
            setRuntimeContext((current) => (
              current?.workspaceId === runtimeContext.workspaceId
                ? { ...current, draftRevision: nextRevision }
                : current
            ));
          }
        }
        if (!quiet) {
          updateSyncPhase('saved');
          window.setTimeout(() => {
            if (syncPhaseRef.current === 'saved') updateSyncPhase('idle');
          }, 2000);
        } else {
          updateSyncPhase('idle');
        }
        return data;
      }).catch((error) => {
        const interpreted = interpretLayoutSaveError(error);
        if (quiet) {
          updateSyncPhase('idle');
          return null;
        }
        if (interpreted.current) setSyncServer(interpreted.current);
        updateSyncPhase(interpreted.phase);
        return null;
      });
    },
    [currentUser?.uid, layoutRole, runtimeContext, tenantId]
  );

  const snapshotFrom = useCallback(
    (nextIcons, extra = {}) => ({
      icons: (nextIcons || []).map(it => ({ ...it, position: it.position ?? 0 })),
      theme: extra.theme ?? themePref,
      gridColumns,
      gridRows,
      iconSize,
      gap,
      showGrid,
    }),
    [themePref, gridColumns, gridRows, iconSize, gap, showGrid]
  );

  useEffect(() => {
    const onLeave = () => {
      if (persistPayloadRef.current && runtimeContext) {
        flushDraftSave(runtimeContext, persistPayloadRef.current);
      }
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [runtimeContext]);

  const applyLoadedLayout = async (data, status) => {
    const loaded = (data?.icons || []).map((it, idx) => ({
      ...it,
      position: Number.isInteger(it.position) ? it.position : idx,
    }));
    setIcons(loaded);
    setThemePref(data?.theme || 'system');
    setGridColumns(clampGridColumns(data?.gridColumns || LAUNCHER_GRID.DEFAULT_COLUMNS));
    setGridRows(clampGridRows(data?.gridRows || LAUNCHER_GRID.DEFAULT_ROWS));
    setIconSize(data?.iconSize || 'medium');
    setGap(data?.gap ?? 2);
    setShowGrid(Boolean(data?.showGrid));
    setBackgroundImageUrl(
      await resolveLauncherBackgroundUrlAsync(currentUser?.uid, data?.backgroundImageUrl)
    );
    setLayoutStatus(loaded.length ? 'ready' : status);
  };

  useEffect(() => {
    const loadKey = `${currentUser?.uid || ''}:${tenantId || ''}:${layoutRole || ''}:${supportSession?.accessLevel || ''}:${layoutRetry}`;
    const dismissalKey = guidanceDismissalKey(currentUser?.uid, tenantId);
    setGuidanceDismissed(Boolean(dismissalKey && localStorage.getItem(dismissalKey) === '1'));
    const identity = `${currentUser?.uid || ''}:${tenantId || ''}`;
    if (
      loadedLayoutKeyRef.current
      && !loadedLayoutKeyRef.current.startsWith(`${identity}:`)
    ) {
      loadedLayoutKeyRef.current = '';
      layoutLoadGen.current += 1;
      setIcons([]);
      setLayoutStatus('loading');
      setLayoutLoaded(false);
      setRuntimeContext(null);
    }
    if (!currentUser?.uid) return undefined;
    if (loadedLayoutKeyRef.current === loadKey) return undefined;
    if (!layoutRole && userData == null && !userClaims) return undefined;
    const gen = ++layoutLoadGen.current;
    setIcons([]);
    setLayoutVerified(false);
    setLayoutStatus('loading');
    setLayoutLoaded(false);
    setRuntimeContext(null);
    let cancelled = false;
    const current = () => !cancelled && gen === layoutLoadGen.current;

    const loadLayout = async () => {
      const supporting = Boolean(supportSession?.tenantId);
      const personalCache = () => {
        if (supporting) return null;
        const scoped = readLayoutFromStorage(currentUser.uid, tenantId);
        if (scoped) return scoped;
        const legacy = readLegacyLayoutFromStorage(currentUser.uid);
        return layoutOwnedByContext(legacy, { tenantId }) ? legacy : null;
      };
      try {
        const wsResult = await resolveAndLoad({ role: layoutRole });
        if (!current()) return;
        if (wsResult?.status === 'unavailable') {
          setLayoutStatus('unavailable');
          setLayoutLoaded(true);
          loadedLayoutKeyRef.current = loadKey;
          return;
        }
        if (wsResult?.status === 'assigned-missing') {
          setRuntimeContext(wsResult.runtimeContext || null);
          setLayoutStatus('assigned-missing');
          setLayoutLoaded(true);
          loadedLayoutKeyRef.current = loadKey;
          return;
        }
        if (wsResult?.usedWorkspace && wsResult.layoutData) {
          setRuntimeContext(wsResult.runtimeContext || legacyRuntimeContext());
          const workspaceId = wsResult.runtimeContext?.workspaceId;
          const publishedVersionId = wsResult.runtimeContext?.workspaceVersionId || null;
          const scoped = supporting ? null : readLayoutFromStorage(currentUser.uid, tenantId);
          const legacy = supporting ? null : readLegacyLayoutFromStorage(currentUser.uid);
          const cached = supporting ? null : (scoped || (
            layoutOwnedByContext(legacy, { tenantId, workspaceId }) ? legacy : null
          ));
          rememberDraftRevision(workspaceId, wsResult.runtimeContext?.draftRevision || 0);
          if (supporting || isDraftEditRole(layoutRole)) {
            const draftLayout = wsResult.layoutData;
            await applyLoadedLayout(draftLayout, 'empty');
            if (!current()) return;
            baseRevisionRef.current = revisionOf(draftLayout) ?? (wsResult.runtimeContext?.draftRevision || 0);
            setLayoutVerified(true);
            updateSyncPhase('idle');
            loadedLayoutKeyRef.current = loadKey;
            setLayoutLoaded(true);
            return;
          }
          let serverPrefs = null;
          try {
            serverPrefs = await loadLayoutFromApi(tenantId);
          } catch {
            serverPrefs = null;
          }
          if (!current()) return;
          const prefsOwned = Boolean(serverPrefs?.stored && tenantId && serverPrefs?.tenantId === tenantId);
          const serverMerged = resolveUserWorkspaceLayout(
            wsResult.layoutData,
            prefsOwned ? serverPrefs : { icons: [] },
            workspaceId,
            currentUser.uid,
            publishedVersionId
          );
          serverMerged.stored = true;
          serverMerged.revision = prefsOwned ? (serverPrefs.revision ?? 0) : 0;
          const localMerged = cached
            ? resolveUserWorkspaceLayout(wsResult.layoutData, cached, workspaceId, currentUser.uid, publishedVersionId)
            : null;
          const decision = reconcileHomePreferences({
            server: serverPrefs == null ? { stored: false, icons: [] } : serverMerged,
            local: localMerged,
            pending: Boolean(cached?.pendingSave),
          });
          setSyncServer(decision.serverLayout || null);
          if (decision.action === 'conflict') {
            await applyLoadedLayout(decision.layout, 'empty');
            if (!current()) return;
            setLayoutVerified(false);
            updateSyncPhase('conflict');
          } else {
            const chosen = decision.layout || localMerged || serverMerged;
            await applyLoadedLayout(chosen, 'empty');
            if (!current()) return;
            const revision = revisionOf(serverMerged) ?? revisionOf(chosen) ?? 0;
            writeLayoutToStorage(currentUser.uid, {
              ...chosen,
              revision,
              pendingSave: false,
              seededFromWorkspaceId: workspaceId,
              seededFromVersionId: publishedVersionId,
            }, tenantId);
            baseRevisionRef.current = revision;
            rememberDraftRevision(workspaceId, revision);
            setLayoutVerified(true);
            updateSyncPhase('idle');
            if (decision.action === 'keep-local') {
              persistSnapshot({
                icons: chosen?.icons || [],
                theme: chosen?.theme,
                gridColumns: chosen?.gridColumns,
                gridRows: chosen?.gridRows,
                iconSize: chosen?.iconSize,
                gap: chosen?.gap,
                showGrid: chosen?.showGrid,
              }, { flush: true, quiet: true });
            }
          }
          loadedLayoutKeyRef.current = loadKey;
          setLayoutLoaded(true);
          return;
        }
      } catch (e) {
        console.warn('[HomeLauncher] Workspace resolve failed', e);
        if (!current()) return;
        setLayoutStatus('unavailable');
        setLayoutLoaded(true);
        loadedLayoutKeyRef.current = loadKey;
        return;
      }

      const cached = personalCache();
      try {
        const data = await loadLayoutFromApi(tenantId);
        if (!current()) return;
        const decision = reconcileHomePreferences({
          server: data,
          local: cached,
          pending: Boolean(cached?.pendingSave),
        });
        setSyncServer(decision.serverLayout || null);
        setRuntimeContext(legacyRuntimeContext());
        if (decision.action === 'conflict') {
          await applyLoadedLayout(decision.layout || cached, 'empty');
          if (!current()) return;
          setLayoutVerified(false);
          updateSyncPhase('conflict');
        } else if (decision.action === 'use-server' && data?.stored && tenantId && data?.tenantId === tenantId && Array.isArray(data.icons)) {
          await applyLoadedLayout(decision.layout, 'empty');
          if (!current()) return;
          writeLayoutToStorage(currentUser.uid, { ...data, pendingSave: false }, tenantId);
          baseRevisionRef.current = revisionOf(data) ?? 0;
          setLayoutVerified(true);
          updateSyncPhase('idle');
        } else if (decision.action === 'use-server') {
          setIcons([]);
          setLayoutStatus('unconfigured');
          setLayoutVerified(true);
          updateSyncPhase('idle');
        } else {
          const chosen = decision.layout || cached;
          await applyLoadedLayout(chosen, 'empty');
          if (!current()) return;
          const revision = revisionOf(data) ?? revisionOf(chosen) ?? 0;
          writeLayoutToStorage(currentUser.uid, { ...chosen, revision, pendingSave: false }, tenantId);
          baseRevisionRef.current = revision;
          setLayoutVerified(true);
          updateSyncPhase('idle');
          persistSnapshot({
            icons: chosen?.icons || [],
            theme: chosen?.theme,
            gridColumns: chosen?.gridColumns,
            gridRows: chosen?.gridRows,
            iconSize: chosen?.iconSize,
            gap: chosen?.gap,
            showGrid: chosen?.showGrid,
          }, { flush: true, quiet: true });
        }
      } catch (e) {
        if (!current()) return;
        if (cached && !String(e?.message || '').includes('(403)')) {
          await applyLoadedLayout(cached, 'empty');
          setLayoutVerified(true);
          updateSyncPhase('idle');
          setRuntimeContext(legacyRuntimeContext());
        } else {
          setIcons([]);
          setLayoutStatus(String(e?.message || '').includes('(403)') ? 'denied' : 'unavailable');
        }
      } finally {
        if (!current()) return;
        loadedLayoutKeyRef.current = loadKey;
        setLayoutLoaded(true);
      }
    };
    loadLayout();
    return () => {
      cancelled = true;
      flushPendingLayoutSave();
    };
  }, [currentUser?.uid, tenantId, layoutRole, supportSession?.tenantId, supportSession?.accessLevel, layoutRetry, userData, userClaims]);

  useEffect(() => {
    if (!currentUser?.uid || !tenantId) return undefined;
    const screenRecord = {
      icons,
      theme: themePref,
      gridColumns,
      gridRows,
      iconSize,
      gap,
      showGrid,
    };
    const editing = () => ['pending', 'saving', 'error', 'conflict', 'recover'].includes(syncPhaseRef.current);
    const onFocus = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (supportSession?.tenantId) return;
      try {
        const draftHome = runtimeContext?.runtimeMode === RUNTIME_MODE.DRAFT_EDIT && runtimeContext?.workspaceId;
        if (draftHome) {
          let draft = null;
          try {
            const response = await getDraftVersion(runtimeContext.workspaceId);
            draft = response?.data || response;
          } catch (error) {
            if (String(error?.message || '').includes('(404)')) return;
            throw error;
          }
          const layout = draft?.layoutSchema || draft;
          const action = remoteHomeOnFocus({
            serverRevision: revisionOf(draft),
            baseRevision: draftRevisionFor(runtimeContext.workspaceId),
            dirty: editing(),
            sameContent: sameHomeRecord(layout, screenRecord),
          });
          if (action === 'reload' && layout) {
            await applyLoadedLayout({ ...layout, revision: revisionOf(draft) }, layout.icons?.length ? 'ready' : 'empty');
            const next = revisionOf(draft) ?? 0;
            baseRevisionRef.current = next;
            rememberDraftRevision(runtimeContext.workspaceId, next);
            setLayoutVerified(true);
            updateSyncPhase('idle');
          } else if (action === 'conflict') {
            setSyncServer({ ...layout, revision: revisionOf(draft) });
            setLayoutVerified(false);
            updateSyncPhase('conflict');
          }
          return;
        }
        const data = await loadLayoutFromApi(tenantId);
        const action = remoteHomeOnFocus({
          serverRevision: revisionOf(data),
          baseRevision: baseRevisionRef.current,
          dirty: editing(),
          sameContent: sameHomeRecord(data, screenRecord),
        });
        if (action === 'reload' && data?.stored && data?.tenantId === tenantId) {
          await applyLoadedLayout(data, data.icons?.length ? 'ready' : 'empty');
          baseRevisionRef.current = revisionOf(data) ?? 0;
          writeLayoutToStorage(currentUser.uid, { ...data, pendingSave: false }, tenantId);
          setLayoutVerified(true);
          updateSyncPhase('idle');
        } else if (action === 'conflict') {
          setSyncServer(data);
          setLayoutVerified(false);
          updateSyncPhase('conflict');
        }
      } catch {
        // Keep the Home on screen. A focus check must not discard it.
      }
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [currentUser?.uid, tenantId, supportSession?.tenantId, runtimeContext, icons, themePref, gridColumns, gridRows, iconSize, gap, showGrid]);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    const onBackgroundChanged = event => {
      if (event?.detail?.uid !== currentUser.uid) return;
      setBackgroundImageUrl(event.detail?.url || '');
    };
    window.addEventListener(LAUNCHER_BACKGROUND_CHANGED, onBackgroundChanged);
    return () => window.removeEventListener(LAUNCHER_BACKGROUND_CHANGED, onBackgroundChanged);
  }, [currentUser?.uid]);

  const resetEditTimeout = useCallback(() => {
    if (!editMode) return;
    if (editTimeoutRef.current) clearTimeout(editTimeoutRef.current);
    editTimeoutRef.current = setTimeout(() => setEditMode(false), EDIT_TIMEOUT_MS);
  }, [editMode]);

  // Auto-exit edit mode after inactivity, but keep extending while interacting/dragging
  useEffect(() => {
    if (!editMode) {
      if (editTimeoutRef.current) clearTimeout(editTimeoutRef.current);
      return;
    }
    resetEditTimeout();
    return () => {
      if (editTimeoutRef.current) clearTimeout(editTimeoutRef.current);
    };
  }, [editMode, resetEditTimeout]);

  const handleReorder = useCallback(
    async newIcons => {
      console.log(
        '[HomeLauncher] handleReorder called with:',
        newIcons.map(i => `${i.id}@${i.position}`)
      );
      if (!currentUser?.uid) {
        throw new Error('Cannot save layout without a signed-in user');
      }
      setLastUpdateTime(Date.now());
      await persistSnapshot(snapshotFrom(newIcons));
    },
    [currentUser?.uid, persistSnapshot, snapshotFrom]
  );

  const dnd = useLauncherDnD({
    icons,
    setIcons,
    editMode,
    canDrag: canArrangeLayout,
    resetEditTimeout,
    gridMax: GRID_MAX,
    handleReorder,
  });

  const handleIconPress = useCallback(
    async icon => {
      if (editMode) return;

      const actionType =
        icon.actionType === 'external'
          ? 'none'
          : icon.actionType || (icon.flowId ? 'flow' : 'none');

      if (actionType === 'askSb') {
        persistShortcutsChrome({ open: true });
        setAskSbFocus((value) => value + 1);
        return;
      }

      if (actionType === 'paging' && icon.pagingZoneId) {
        setActivePagingIcon(icon);
        setRtpDialogOpen(true);
        return;
      }

      if (actionType === 'sipPaging' && icon.sipPagingZoneId) {
        try {
          const zones = await getTenantSipPagingMyZones(tenantId);
          const zone = zones.find(z => z.id === icon.sipPagingZoneId);
          if (!zone) {
            toast.error('SIP paging zone not found or not accessible');
            return;
          }
          if (!zone.sipUri) {
            toast.error('SIP paging zone has no SIP URI configured');
            return;
          }
          setActiveSipPagingIcon(icon);
          setActiveSipUri(zone.sipUri);
          setSipDialogOpen(true);
        } catch (e) {
          console.error('[HomeLauncher] Failed to load SIP paging zone', e);
          toast.error('Failed to load SIP paging zone');
        }
        return;
      }

      if (actionType === 'imageDisplay' && icon.imageDisplayActionId) {
        setActiveImageDisplayIcon(icon);
        setImageDisplayDialogOpen(true);
        return;
      }

      if (actionType === 'imageDisplay' && !icon.imageDisplayActionId) {
        toast.error('Image Display is not configured for this button');
        return;
      }

      if (actionType === 'collaboration') {
        if (!icon.collaborationWorkspaceId) {
          toast.error('Collaboration button has no workspace configured. Please edit the button.');
          return;
        }
        try {
          const workspace = await getCollaborationWorkspace(
            tenantId,
            icon.collaborationWorkspaceId
          );
          if (!workspace) {
            toast.error('Collaboration workspace not found');
            return;
          }
          setActiveCollaborationData({
            workspaceId: workspace.id,
            workspace,
            workspaceName: workspace.name,
          });
          setCollaborationModalOpen(true);
        } catch (e) {
          console.error('[HomeLauncher] Failed to load collaboration workspace', e);
          toast.error('Failed to open collaboration workspace');
        }
        return;
      }

      if (actionType === 'phone') {
        if (!isPhoneEnabledForUser(userData, role)) {
          toast.error('Browser phone is not enabled for your account.');
          return;
        }
        setPhoneOpen(true);
        return;
      }

      if (actionType === 'reminders') {
        if (!isRemindersEnabledForUser(userData)) {
          toast.error('Reminders and timers are not enabled for your account.');
          return;
        }
        setRemindersOpen(true);
        return;
      }

      if (actionType === 'emailRem') {
        if (!isEmailRemEnabledForUser(userData)) {
          toast.error('Email reminders are not enabled for your account.');
          return;
        }
        setEmailRemOpen(true);
        return;
      }

      if (actionType === 'action') {
        if (!icon.actionId) {
          toast.error(
            'This button has no action assigned. Please edit the button and select an action.'
          );
          return;
        }
        try {
          await executeDirectAction(icon, tenantId);
          toast.success(`${icon.label} triggered`);
        } catch (e) {
          console.error('[HomeLauncher] Action dispatch failed', e);
          toast.error(e.message || 'Failed to run action');
        }
        return;
      }

      if (actionType === 'contact_list') {
        if (!icon.contactListId) {
          toast.error('Contact List button has no list configured. Please edit the button.');
          return;
        }
        try {
          const resp = await getContactList(tenantId, icon.contactListId);
          const contactList = resp?.data?.contactList || resp?.contactList || resp;
          if (!contactList?.id) {
            toast.error('Contact list not found or has been deleted.');
            return;
          }
          if (contactList.calling?.enabled) {
            const sessionId =
              typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
            setActiveContactListCall({ contactList, sessionId });
            setContactListCallOpen(true);
          } else {
            setActiveContactListId(contactList.id);
            setContactListDialogOpen(true);
          }
        } catch (e) {
          console.error('[HomeLauncher] Failed to load contact list', e);
          toast.error('Failed to open contact list');
        }
        return;
      }

      if (actionType === 'openScreenSaver') {
        openScreenSaver(navigate, {
          siteId: preferredSiteId,
          returnTo: ROUTES.HOME_LAUNCHER,
        });
        return;
      }

      if (!icon.flowId) {
        if (icon.id === 'settings') {
          navigate('/home/settings');
          return;
        }
        toast.info('No flow assigned');
        return;
      }

      const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (uuidLike.test(String(icon.flowId))) {
        console.error(
          '[HomeLauncher] Button misconfigured: flowId is UUID-like (likely assignment id, not flow id)',
          {
            iconId: icon.id,
            flowId: icon.flowId,
          }
        );
        toast.error(
          'This button is misconfigured (invalid Flow ID). Please edit the button and re-select the Flow.'
        );
        return;
      }
      try {
        const traceId =
          (globalThis.crypto?.randomUUID && globalThis.crypto.randomUUID()) ||
          `${Date.now()}_${Math.random().toString(16).slice(2)}`;

        const payload = {
          flowId: icon.flowId,
          tenantId,
          traceId,
          correlationId: traceId,
          source: 'home_launcher',
          trigger: 'launcher_button',
          launcherButtonId: icon.id,
          launcherButtonLabel: icon.label,
        };

        console.info('[HomeLauncher] Triggering flow', {
          traceId,
          flowId: icon.flowId,
          tenantId,
          iconId: icon.id,
          label: icon.label,
        });

        const res = await executeCommand(icon.flowId, payload);
        if (isFailedExecutionResult(res)) {
          throw new Error(
            flowDisabledUserMessage(null, res) ||
              flowExecutionFailureMessage(res) ||
              res.message ||
              res.error ||
              'Failed to trigger flow'
          );
        }
        if (res?.executionStatus === 'waiting') {
          toast.info(waitingDisplayLabel(res.resumeAt || res.details?.resumeAt));
          return;
        }

        const actionResults = res?.details?.actionResults || [];
        const incidentIds = [];
        actionResults.forEach(r => {
          const d = r?.details || {};
          const one = d?.incidentId;
          const many = d?.incidentIds;
          if (one) incidentIds.push(one);
          if (Array.isArray(many)) incidentIds.push(...many);
        });
        const uniqIncidentIds = Array.from(new Set(incidentIds.filter(Boolean)));

        if (uniqIncidentIds.length > 0) {
          toast.success(`${icon.label} triggered (incidents: ${uniqIncidentIds.length})`);
          console.info('[HomeLauncher] Flow triggered incidents', {
            traceId,
            incidentIds: uniqIncidentIds,
          });

          if (openIncidentById) {
            try {
              const opened = await openIncidentById(uniqIncidentIds[0]);
              if (!opened) {
                console.warn('[HomeLauncher] Incident created but could not be opened', {
                  traceId,
                  incidentId: uniqIncidentIds[0],
                });
              }
            } catch (e) {
              console.warn('[HomeLauncher] Failed to open incident modal after flow', {
                traceId,
                incidentId: uniqIncidentIds[0],
                error: e,
              });
            }
          }
        } else {
          toast.success(`${icon.label} triggered`);
          console.info('[HomeLauncher] Flow trigger completed (no incidentIds in response)', {
            traceId,
            summary: {
              executionStatus: res?.executionStatus,
              success: res?.success,
              flowId: res?.details?.flowId,
              executionId: res?.details?.executionId,
              totalActions: res?.details?.totalActions,
              successfulActions: res?.details?.successfulActions,
            },
          });
        }
      } catch (e) {
        console.error('[HomeLauncher] Flow execution failed', e);
        toast.error(flowDisabledUserMessage(e) || e.message || 'Failed to trigger flow');
      }
    },
    [editMode, tenantId, navigate, preferredSiteId, openIncidentById, userData, role, persistShortcutsChrome]
  );

  const handleSaveIcon = useCallback(
    async changes => {
      if (!editingIcon) return;
      const {
        label,
        flowId,
        actionId,
        actionType,
        pagingZoneId,
        micDeviceId,
        sipPagingZoneId,
        imageDisplayActionId,
        collaborationWorkspaceId,
        contactListId,
        iconType,
        iconKey,
        iconText,
        iconBg,
        iconImageAssetId,
      } = changes;
      const updated = icons.map(it => {
        if (it.id !== editingIcon.id) return it;
        const nextType = iconType || it.iconType || 'builtin';
        return {
          ...it,
          label,
          flowId,
          actionId,
          actionType,
          pagingZoneId,
          micDeviceId,
          sipPagingZoneId,
          imageDisplayActionId,
          collaborationWorkspaceId,
          contactListId,
          iconType: nextType,
          iconKey: nextType === 'builtin' ? iconKey || null : null,
          iconText: nextType === 'text' ? iconText || null : null,
          iconBg: nextType === 'text' ? iconBg || null : null,
          iconImageAssetId: nextType === 'image' ? iconImageAssetId || null : null,
        };
      });
      setIcons(updated);
      try {
        if (currentUser?.uid) {
          await persistSnapshot(snapshotFrom(updated), { flush: true });
        }
      } catch (error) {
        setIcons(icons);
        toast.error(error?.message || 'Could not save the button image');
        return;
      }
      setEditingIcon(null);
      toast.success('Button updated');
    },
    [editingIcon, icons, currentUser?.uid, persistSnapshot, snapshotFrom]
  );

  const handleDeleteById = useCallback(
    async icon => {
      if (!canConfigureHome) return;
      if (!icon || icon.id === 'settings') return;
      const filtered = icons.filter(it => it.id !== icon.id);
      // keep positions as-is for remaining icons
      setIcons(filtered);
      if (currentUser?.uid) {
        persistSnapshot(snapshotFrom(filtered), { flush: true });
      }
      if (editingIcon?.id === icon.id) setEditingIcon(null);
      toast.success('Button deleted');
    },
    [canConfigureHome, icons, editingIcon, currentUser?.uid, persistSnapshot, snapshotFrom]
  );

  const handleAddIcon = useCallback(async (requestedPosition) => {
    if (!canConfigureHome || !layoutVerified) return;
    if (icons.length >= GRID_MAX) return;
    const newIcon = createNewIcon(icons, GRID_MAX, requestedPosition);
    const updated = [...icons, newIcon];
    setIcons(updated);
    if (currentUser?.uid) {
      persistSnapshot(snapshotFrom(updated), { flush: true });
    }
    setEditingIcon(newIcon);
  }, [canConfigureHome, layoutVerified, icons, GRID_MAX, currentUser?.uid, persistSnapshot, snapshotFrom]);

  useEffect(() => {
    if (!canConfigureHome && editMode) setEditMode(false);
  }, [canConfigureHome, editMode]);

  useEffect(() => {
    const onAdd = () => {
      handleAddIcon();
    };
    window.addEventListener(ADD_HOME_ICON_EVENT, onAdd);
    return () => window.removeEventListener(ADD_HOME_ICON_EVENT, onAdd);
  }, [handleAddIcon]);

  const handleDeleteIcon = useCallback(async () => {
    if (!editingIcon) return;
    if (editingIcon.id === 'settings') {
      setEditingIcon(null);
      return;
    }
    const filtered = icons.filter(it => it.id !== editingIcon.id);
    setIcons(filtered);
    if (currentUser?.uid) {
      persistSnapshot(snapshotFrom(filtered), { flush: true });
    }
    setEditingIcon(null);
    toast.success('Button deleted');
  }, [editingIcon, icons, currentUser?.uid, persistSnapshot, snapshotFrom]);

  const handleThemeChange = async next => {
    setThemePref(next);
    if (!currentUser?.uid) return;
    persistSnapshot(snapshotFrom(icons, { theme: next }));
  };

  const designerHome = canConfigureTenantHome({
    role: supportSession ? 'tenant_admin' : role,
    dealerActive: dealerAdmin.active,
  });
  const homeConfigured = layoutStatus === 'ready' || layoutStatus === 'empty';
  const homeGuideProgress = homeGuidanceProgress({
    homeConfigured,
    flowState,
    flowCount: flowById.size,
    shortcutState: shortcuts.loading
      ? 'loading'
      : (shortcuts.error ? (supportSession ? 'error' : 'unavailable') : 'ready'),
    shortcutCount: (shortcuts.allShortcuts || []).filter((item) => (
      !supportSession || item.scope === 'shared'
    )).length,
  });
  const showHomeWelcome = layoutStatus === 'unconfigured' || layoutStatus === 'empty';
  const showHomeGuide = designerHome
    && !guidanceDismissed
    && !homeGuidanceFinished(homeGuideProgress)
    && showHomeWelcome;
  const showHomePreparing = !designerHome && layoutStatus === 'unconfigured';
  const homeLayoutNotice = layoutStatus === 'unavailable'
    ? 'Home could not be loaded. Nothing was changed.'
    : layoutStatus === 'assigned-missing'
      ? 'A Home is assigned, but its layout could not be loaded.'
      : layoutStatus === 'denied'
        ? 'You do not have access to a Home for this workspace.'
        : '';
  const dismissHomeGuidance = () => {
    const key = guidanceDismissalKey(currentUser?.uid, tenantId);
    if (key) localStorage.setItem(key, '1');
    setGuidanceDismissed(true);
  };
  const retryHomeLoad = () => {
    loadedLayoutKeyRef.current = '';
    setLayoutRetry((value) => value + 1);
    shortcuts.retryLoad();
  };

  useEffect(() => {
    if (!supportSession?.tenantId) return;
    if (shortcuts.error !== 'Shortcuts for this tenant are no longer available') return;
    setIcons([]);
    setLayoutStatus('denied');
    loadedLayoutKeyRef.current = '';
  }, [supportSession?.tenantId, shortcuts.error]);

  const homeNoticeVisible = Boolean(syncPhase && syncPhase !== 'idle');

  useLayoutEffect(() => {
    const page = pageRef.current;
    const slot = noticeSlotRef.current;
    const root = document.documentElement;
    if (!page || !slot) return undefined;
    const publish = () => {
      const top = page.getBoundingClientRect().top;
      const bottom = slot.getBoundingClientRect().bottom - top;
      root.style.setProperty('--sb-home-notice-bottom', `${Math.max(0, Math.round(bottom))}px`);
    };
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(slot);
    window.addEventListener('resize', publish);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', publish);
      root.style.removeProperty('--sb-home-notice-bottom');
    };
  }, [homeNoticeVisible, narrowWorkspace]);

  return (
    <Box
      ref={pageRef}
      sx={{
        height: '100vh',
        minWidth: shortcutsOpen && dockShortcuts ? 0 : LAUNCHER_GRID.MIN_PAGE_WIDTH,
        minHeight: LAUNCHER_GRID.MIN_PAGE_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        bgcolor: isDark ? '#0b0b0d' : '#f5f7fb',
        transition: 'background-color 0.2s ease',
        boxSizing: 'border-box',
        pl: 'var(--sb-workspace-rail-width, 0px)',
        pt: homeNoticeVisible && !narrowWorkspace ? 0 : 'var(--sb-workspace-chrome-top, 0px)',
        pb: 0,
        overflow: 'hidden',
      }}
    >
      <SupportContextBanner />
      <Box ref={noticeSlotRef} sx={{ flexShrink: 0 }}>
        <HomePersistenceNotice
          phase={syncPhase === 'saved' ? 'saved' : syncPhase}
          onRetry={() => persistSnapshot(snapshotFrom(icons), { flush: true })}
          onUseServer={async () => {
          if (!currentUser?.uid) return;
          const chosen = syncServer || snapshotFrom(icons);
          const local = readLayoutFromStorage(currentUser.uid, tenantId);
          if (local) {
            try {
              localStorage.setItem(`launcher_layout_recovery_${currentUser.uid}_${tenantId}`, JSON.stringify(local));
            } catch {
              // The saved server Home still replaces the screen. The browser copy remains in memory until then.
            }
          }
          await applyLoadedLayout(chosen, chosen.icons?.length ? 'ready' : 'empty');
          const revision = revisionOf(chosen) ?? 0;
          writeLayoutToStorage(currentUser.uid, { ...chosen, revision, pendingSave: false }, tenantId);
          baseRevisionRef.current = revision;
          if (runtimeContext?.workspaceId) rememberDraftRevision(runtimeContext.workspaceId, revision);
          setLayoutVerified(true);
          updateSyncPhase('idle');
        }}
        onKeepLocal={() => {
          const next = revisionOf(syncServer) ?? baseRevisionRef.current;
          baseRevisionRef.current = next;
          if (runtimeContext?.workspaceId) rememberDraftRevision(runtimeContext.workspaceId, next);
          writeLayoutToStorage(currentUser?.uid, { ...snapshotFrom(icons), revision: next, pendingSave: false }, tenantId);
          setLayoutVerified(true);
          updateSyncPhase('idle');
          persistSnapshot(snapshotFrom(icons), { flush: true, quiet: true });
        }}
        />
      </Box>
      {resolvedBackground ? (
        <Box
          component="img"
          src={resolvedBackground}
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      {/* <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2" color={isDark ? 'grey.400' : 'text.secondary'}>Launcher</Typography>
        {editMode ? (
          <Button size="small" variant="contained" onClick={() => setEditMode(false)}>Done</Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={() => setEditMode(true)}>Edit</Button>
            <Button size="small" onClick={() => setSettingsOpen(true)}>Settings</Button>
          </Box>
        )}
      </Box> */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          px: 2,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ pointerEvents: 'auto' }}>
          {editMode ? (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => setEditMode(false)}
            >
              Done
            </Button>
          ) : null}
        </Box>
        <IconButton
          size="small"
          color={shortcutsOpen ? 'primary' : 'default'}
          onClick={() => persistShortcutsChrome({ open: !shortcutsOpen })}
          aria-pressed={shortcutsOpen}
          aria-label="Shortcuts"
          sx={{
            position: 'fixed',
            top: 'calc(var(--sb-home-notice-bottom, var(--sb-workspace-chrome-top, 0px)) + 8px)',
            right:
              shortcutsOpen && dockShortcuts
                ? `calc(${shortcutsWidth}px + 10px)`
                : 10,
            zIndex: 1460,
            pointerEvents: 'auto',
            bgcolor: shortcutsOpen ? 'primary.main' : '#fff',
            color: shortcutsOpen ? '#fff' : 'text.primary',
            boxShadow: '0 1px 4px rgba(15, 23, 42, 0.16)',
            '&:hover': {
              bgcolor: shortcutsOpen ? 'primary.dark' : 'grey.100',
            },
          }}
        >
          <BookmarkBorderIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'row',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            overflow: 'auto',
          }}
        >
          <DndContext
            sensors={dnd.sensors}
            collisionDetection={closestCenter}
            onDragStart={dnd.onDragStart}
            onDragMove={dnd.onDragMove}
            onDragEnd={dnd.onDragEnd}
            onDragCancel={dnd.onDragCancel}
          >
            <Box sx={{ height: '100%', minWidth: 0 }}>
              {homeSetupCardVisible({
                guide: showHomeGuide,
                preparing: showHomePreparing,
                notice: homeLayoutNotice,
              }) ? (
                <HomeSetupGuide
                  tenantName={supportSession?.name || dealerAdmin.tenantName || userData?.tenantName || ''}
                  role={supportSession ? 'tenant_admin' : role}
                  dealerActive={dealerAdmin.active}
                  progress={homeGuideProgress}
                  preparing={showHomePreparing}
                  notice={homeLayoutNotice}
                  showSteps={showHomeGuide}
                  onEnterSupport={!supportSession && role === 'superadmin' && layoutStatus === 'denied' ? () => {
                    writeSupportedTenantSession({
                      tenantId,
                      name: userData?.tenantName || '',
                      accessLevel: 'manage',
                    });
                  } : null}
                  onDismiss={showHomeGuide ? dismissHomeGuidance : null}
                  onRetry={(supportSession && (shortcuts.error || layoutStatus === 'unavailable' || flowState === 'error')) ? retryHomeLoad : null}
                />
              ) : null}
              <IconGrid
                slots={slots}
                editMode={editMode}
                canDrag={canArrangeLayout}
                isLayoutDragging={dnd.isDragging}
                suppressActivation={dnd.suppressClick}
                onLongPress={() => {
                  if (canConfigureHome) setEditMode(true);
                }}
                onIconClick={icon => {
                  if (dnd.suppressClick || dnd.isDragging) return;
                  handleIconPress(icon);
                }}
                onEditRequest={icon => {
                  if (canConfigureHome) setEditingIcon(icon);
                }}
                onDeleteRequest={handleDeleteById}
                onAddRequest={handleAddIcon}
                onEmptyClick={handleAddIcon}
                canAdd={layoutVerified && canConfigureHome && icons.length < GRID_MAX && layoutStatus !== 'unconfigured' && layoutStatus !== 'unavailable' && layoutStatus !== 'assigned-missing' && layoutStatus !== 'loading'}
                gridMax={GRID_MAX}
                dark={isDark}
                columns={GRID_COLUMNS}
                iconSize={iconSize}
                gap={gap}
                showGrid={showGrid}
                rows={GRID_ROWS}
                labelOverBackground
                fluidCells={shortcutsOpen && dockShortcuts}
                tenantId={tenantId}
              />
            </Box>
          </DndContext>
        </Box>
        {dockShortcuts && shortcutsOpen ? (
          <>
            <Box aria-hidden sx={{ width: shortcutsWidth, flexShrink: 0 }} />
            <ShortcutsPanel
              open
              variant="docked"
              width={shortcutsWidth}
              onWidthChange={width => persistShortcutsChrome({ width })}
              onClose={() => persistShortcutsChrome({ open: false })}
              shortcuts={shortcuts}
              tenantId={tenantId}
              askSbFocus={askSbFocus}
            />
          </>
        ) : null}
      </Box>
      <Drawer
        anchor="right"
        open={!dockShortcuts && shortcutsOpen}
        onClose={() => persistShortcutsChrome({ open: false })}
        PaperProps={{ sx: { width: shortcutsWidth, maxWidth: '100%' } }}
      >
        <ShortcutsPanel
          open={!dockShortcuts && shortcutsOpen}
          variant="drawer"
          width={shortcutsWidth}
          onWidthChange={width => persistShortcutsChrome({ width })}
          onClose={() => persistShortcutsChrome({ open: false })}
          shortcuts={shortcuts}
          tenantId={tenantId}
          askSbFocus={askSbFocus}
        />
      </Drawer>

      <Box
        sx={{
          position: 'fixed',
          left: '50%',
          bottom: 96,
          transform: 'translateX(-50%)',
          zIndex: 1200,
          display: 'none',
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setRtpManualDialogOpen(true)}
          size="medium"
        >
          Manual RTP Paging
        </Button>
      </Box>

      <ButtonAdminModal
        open={canConfigureHome && !!editingIcon}
        icon={editingIcon}
        imageScope={
          runtimeContext?.runtimeSource === 'workspace'
          && runtimeContext?.workspaceId
          && isDraftEditRole(layoutRole)
            ? 'shared'
            : 'personal'
        }
        onClose={() => setEditingIcon(null)}
        onSave={handleSaveIcon}
        onDelete={handleDeleteIcon}
      />

      <RtpPagingDialog
        open={rtpDialogOpen}
        onClose={() => {
          setRtpDialogOpen(false);
          setActivePagingIcon(null);
        }}
        presetZoneId={activePagingIcon?.pagingZoneId || null}
        presetZoneName={activePagingIcon?.label || null}
        micDeviceId={activePagingIcon?.micDeviceId || null}
      />

      <RtpPagingAdvancedDialog
        open={rtpManualDialogOpen}
        onClose={() => setRtpManualDialogOpen(false)}
      />

      <SipPagingDialog
        open={sipDialogOpen}
        onClose={() => {
          setSipDialogOpen(false);
          setActiveSipPagingIcon(null);
          setActiveSipUri(null);
        }}
        presetZoneId={activeSipPagingIcon?.sipPagingZoneId || null}
        presetZoneName={activeSipPagingIcon?.label || null}
        sipUri={activeSipUri}
      />

      <ImageDisplayModal
        open={imageDisplayDialogOpen}
        onClose={() => {
          setImageDisplayDialogOpen(false);
          setActiveImageDisplayIcon(null);
        }}
        actionLabel={activeImageDisplayIcon?.label || 'Image Display'}
        loading={imageDisplayLoading}
        onSubmit={async ({ imageBase64, overlayText, fitMode, filename }) => {
          setImageDisplayLoading(true);
          try {
            if (!tenantId) {
              throw new Error('Tenant is required');
            }
            if (!activeImageDisplayIcon?.imageDisplayActionId) {
              throw new Error('Image Display action is not configured');
            }

            const actions = await getMyImageDisplayActions(tenantId);
            const selectedAction =
              actions.find(a => a && a.id === activeImageDisplayIcon.imageDisplayActionId) || null;
            const targetDeviceId = selectedAction?.targetDeviceId || null;
            if (!targetDeviceId) {
              throw new Error('Image Display action has no target device');
            }

            const devices = await getTenantDevices(tenantId);
            const device = devices.find(d => d && d.id === targetDeviceId) || null;
            const deviceIp = device?.ipAddress || device?.ip || device?.ip_address || null;
            if (!deviceIp) {
              throw new Error('deviceIp is required for image_display');
            }
            const agentId = device?.agentId || device?.agent_id || null;
            const segmentId =
              device?.segmentId ||
              device?.segment_id ||
              device?.segment ||
              device?.properties?.segment ||
              null;

            const dataUrl = String(imageBase64 || '').trim();
            if (!dataUrl) {
              throw new Error('Image is required');
            }
            const ctMatch = dataUrl.match(/^data:([^;]+);base64,/i);
            const contentType = (ctMatch && ctMatch[1]) || 'image/png';

            if (!isOption2ImageDisplayEnabled()) {
              const resp = await dispatchAction({
                tenantId,
                actionId: activeImageDisplayIcon.imageDisplayActionId,
                context: {
                  imageBase64: dataUrl,
                  overlayText,
                  fitMode,
                  filename,
                  deviceIp,
                  ...(agentId ? { agentId } : {}),
                },
              });
              const ok = Boolean(resp && resp.success && resp.result && resp.result.success);
              if (!ok) {
                const msg =
                  (resp && resp.result && (resp.result.message || resp.result.error)) ||
                  'Dispatch failed';
                throw new Error(msg);
              }
              toast.success('Image sent to display');
              setImageDisplayDialogOpen(false);
              setActiveImageDisplayIcon(null);
              return;
            }

            const uploadMeta = await createImageDisplayUploadUrl({
              tenantId,
              filename,
              contentType,
            });
            if (!uploadMeta || !uploadMeta.uploadUrl || !uploadMeta.downloadUrl) {
              throw new Error('Failed to create upload URL');
            }

            await putImageToSignedUrl({
              uploadUrl: uploadMeta.uploadUrl,
              headers: uploadMeta.requiredHeaders || { 'Content-Type': contentType },
              dataUrl,
              contentType,
            });

            console.log('[HomeLauncher][ImageDisplay] Pre-dispatch', {
              actionId: activeImageDisplayIcon.imageDisplayActionId,
              targetDeviceId,
              deviceIp,
              agentId,
            });

            const resp = await dispatchImageDisplayCommand({
              tenantId,
              deviceId: targetDeviceId,
              ...(segmentId ? { segmentId } : {}),
              ...(agentId ? { agentId } : {}),
              imageUrl: uploadMeta.downloadUrl,
              filename,
              overlayText,
            });
            const ok = Boolean(resp && resp.success);
            if (!ok) {
              const msg = (resp && (resp.error || resp.message)) || 'Dispatch failed';
              throw new Error(msg);
            }
            toast.success('Image sent to display');
            setImageDisplayDialogOpen(false);
            setActiveImageDisplayIcon(null);
          } catch (e) {
            console.error('[HomeLauncher] Image display failed', e);
            toast.error(
              e?.message
                ? `Failed to send image to display: ${e.message}`
                : 'Failed to send image to display'
            );
          } finally {
            setImageDisplayLoading(false);
          }
        }}
      />

      <CollaborationWorkspaceModal
        open={collaborationModalOpen}
        onClose={() => {
          setCollaborationModalOpen(false);
          setActiveCollaborationData(null);
        }}
        collaborationData={activeCollaborationData}
      />

      <ContactListViewerDialog
        open={contactListDialogOpen}
        onClose={() => {
          setContactListDialogOpen(false);
          setActiveContactListId(null);
        }}
        contactListId={activeContactListId}
        tenantId={tenantId}
      />

      <ContactListCallDialog
        open={contactListCallOpen}
        onClose={() => {
          setContactListCallOpen(false);
          setActiveContactListCall(null);
        }}
        contactList={activeContactListCall?.contactList || null}
        tenantId={tenantId}
        sessionId={activeContactListCall?.sessionId || ''}
      />

      <PhoneOverlay
        open={phoneOpen}
        onClose={() => setPhoneOpen(false)}
        userData={userData}
        role={role}
      />

      <RemindersOverlay
        open={remindersOpen}
        onClose={() => setRemindersOpen(false)}
        userData={userData}
        tenantId={tenantId}
      />

      <EmailRemOverlay
        open={emailRemOpen}
        onClose={() => setEmailRemOpen(false)}
        userData={userData}
      />
    </Box>
  );
}
